import { Box, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

// A small markdown subset (paragraphs, - and 1. lists, ##/### headings, --- rules, pipe tables,
// **bold**) rendered with the app's own typography. Shared by G.AI answers and release notes.

export function renderInlineMarkdown(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <Box component="strong" key={`${part}-${index}`} sx={{ fontWeight: 700 }}>{part.slice(2, -2)}</Box>;
    }
    return part;
  });
}

function isTableLine(line) {
  return /^\s*\|.*\|\s*$/.test(line);
}

function isTableSeparator(line) {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function parseTable(lines, startIndex) {
  const tableLines = [];
  let index = startIndex;
  while (index < lines.length && isTableLine(lines[index])) {
    tableLines.push(lines[index]);
    index += 1;
  }

  if (tableLines.length < 2 || !isTableSeparator(tableLines[1])) {
    return null;
  }

  const parseRow = (line) => line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());

  return {
    nextIndex: index,
    headers: parseRow(tableLines[0]),
    rows: tableLines.slice(2).map(parseRow),
  };
}

function splitInlineNumberedList(text) {
  const match = String(text).match(/^(.*?:)\s+(\d+\.\s+.*)$/);
  if (!match) return null;

  const [, intro, numberedText] = match;
  const parts = numberedText
    .split(/\s+(?=\d+\.\s+)/)
    .map((item) => item.replace(/^\d+\.\s+/, '').trim())
    .filter(Boolean);

  if (parts.length < 2) return null;
  return { intro, items: parts };
}

export function renderMarkdownBlocks(answer) {
  const lines = String(answer).split('\n');
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    const table = parseTable(lines, index);
    if (table) {
      blocks.push(
        <TableContainer
          key={`table-${index}`}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            my: 1.5,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {table.headers.map((header, headerIndex) => (
                  <TableCell
                    key={`${header}-${headerIndex}`}
                    sx={{
                      bgcolor: 'lightGrey.light',
                      fontWeight: 700,
                      color: 'text.primary',
                    }}
                  >
                    {renderInlineMarkdown(header)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {table.rows.map((row, rowIndex) => (
                <TableRow key={`row-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <TableCell key={`${cell}-${cellIndex}`}>
                      {renderInlineMarkdown(cell)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
      index = table.nextIndex;
      continue;
    }

    if (/^-{3,}$/.test(trimmed)) {
      blocks.push(<Divider key={`divider-${index}`} sx={{ my: 1.5 }} />);
      index += 1;
      continue;
    }

    if (trimmed.startsWith('### ')) {
      blocks.push(
        <Typography key={`h3-${index}`} variant="h6" sx={{ mt: 2, mb: 0.75, fontWeight: 700 }}>
          {renderInlineMarkdown(trimmed.replace(/^###\s+/, ''))}
        </Typography>
      );
      index += 1;
      continue;
    }

    if (trimmed.startsWith('## ')) {
      blocks.push(
        <Typography key={`h2-${index}`} variant="h5" sx={{ mt: 0.5, mb: 1.5, fontWeight: 700 }}>
          {renderInlineMarkdown(trimmed.replace(/^##\s+/, ''))}
        </Typography>
      );
      index += 1;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^[-*]\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^[-*]\s+/, ''));
        index += 1;
      }
      blocks.push(
        <Box component="ul" key={`list-${index}`} sx={{ pl: 3, my: 1 }}>
          {items.map((item, itemIndex) => (
            <Box component="li" key={`${item}-${itemIndex}`} sx={{ mb: 0.5 }}>
              <Typography variant="body1">{renderInlineMarkdown(item)}</Typography>
            </Box>
          ))}
        </Box>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, ''));
        index += 1;
      }
      blocks.push(
        <Box component="ol" key={`ordered-list-${index}`} sx={{ pl: 3, my: 1 }}>
          {items.map((item, itemIndex) => (
            <Box component="li" key={`${item}-${itemIndex}`} sx={{ mb: 0.75, pl: 0.5 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>{renderInlineMarkdown(item)}</Typography>
            </Box>
          ))}
        </Box>
      );
      continue;
    }

    const paragraph = [trimmed];
    index += 1;
    while (
      index < lines.length &&
      lines[index].trim() &&
      !isTableLine(lines[index]) &&
      !/^#{2,3}\s+/.test(lines[index].trim()) &&
      !/^[-*]\s+/.test(lines[index].trim()) &&
      !/^\d+\.\s+/.test(lines[index].trim()) &&
      !/^-{3,}$/.test(lines[index].trim())
    ) {
      paragraph.push(lines[index].trim());
      index += 1;
    }

    const paragraphText = paragraph.join(' ');
    const inlineNumberedList = splitInlineNumberedList(paragraphText);
    if (inlineNumberedList) {
      blocks.push(
        <Box key={`inline-ordered-${index}`} sx={{ mb: 1.25 }}>
          <Typography variant="body1" sx={{ mb: 0.75, lineHeight: 1.7 }}>
            {renderInlineMarkdown(inlineNumberedList.intro)}
          </Typography>
          <Box component="ol" sx={{ pl: 3, my: 0 }}>
            {inlineNumberedList.items.map((item, itemIndex) => (
              <Box component="li" key={`${item}-${itemIndex}`} sx={{ mb: 0.75, pl: 0.5 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>{renderInlineMarkdown(item)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      );
      continue;
    }

    blocks.push(
      <Typography key={`p-${index}`} variant="body1" sx={{ mb: 1.25, lineHeight: 1.7 }}>
        {renderInlineMarkdown(paragraphText)}
      </Typography>
    );
  }

  return blocks;
}
